import { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Heart, Users, Church, BookOpen } from 'lucide-react';
import usePageBanner from '../../hooks/usePageBanner';
import { AutoTranslate } from '../../hooks/useAutoTranslate';

const KallelsePage = () => {
  const { t, language, isRTL } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('kallelse', language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || "https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1920"}
          alt="Kallelser"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Andliga Kallelser</AutoTranslate>}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Att svara på Guds kallelse</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <Card className="border-0 shadow-xl card-hover mb-8">
            <CardContent className="p-8">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>Guds kallelse till dig</AutoTranslate>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <AutoTranslate>
                      Alla människor är kallade att bygga Guds rike på jorden. Samtidigt kallar Jesus vissa människor till ett alldeles särskilt ansvar att förkunna det glada budskapet om Faderns kärlek till sin skapelse och att be för hela mänskligheten.
                    </AutoTranslate>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <AutoTranslate>
                      Präst- och ordenskallelser är visserligen något alldeles speciellt, men man behöver inte vara annorlunda för att bli kallad. Ibland tror vi att en sådan kallelse kommer plötsligt - men ofta växer insikten om att Gud kallar en till präst- eller ordensliv fram i takt med att man blir alltmer medveten om sin innersta längtan och upptäcker små tecken som Gud ger på vägen.
                    </AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Prayer */}
          <Card className="border-0 shadow-xl card-hover mb-8 bg-gradient-to-r from-blue-50 to-purple-50">
            <CardContent className="p-8">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>Bönens betydelse</AutoTranslate>
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    <AutoTranslate>
                      När din bön blir personlig växer du i medvetenheten om Guds personliga kärlek till dig och hans inbjudan till att göra denna kärlek känd och ge den vidare till alla.
                    </AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Types of Vocations */}
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6 text-center">
            <AutoTranslate>Olika kallelser</AutoTranslate>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Priesthood */}
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Church className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900">
                    <AutoTranslate>Prästkallelse</AutoTranslate>
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <AutoTranslate>
                    Prästen är kallad att förkunna evangeliet, fira sakramenten och leda Guds folk. Han agerar i Kristi person och tjänar församlingen genom att ge sig helt åt Gud och hans kyrka.
                  </AutoTranslate>
                </p>
              </CardContent>
            </Card>

            {/* Religious Life */}
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900">
                    <AutoTranslate>Ordensliv</AutoTranslate>
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <AutoTranslate>
                    Ordenssystrar och ordensbröder lever i gemenskap och följer de evangeliska råden: fattigdom, kyskhet och lydnad. De vittnar om Guds rike genom bön och tjänst.
                  </AutoTranslate>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <Card className="border-0 shadow-xl card-hover bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-serif font-bold mb-4">
                <AutoTranslate>Känner du en kallelse?</AutoTranslate>
              </h2>
              <p className="text-purple-100 leading-relaxed mb-4">
                <AutoTranslate>
                  Att bli präst, ordenssyster eller ordensbroder kan tyckas vara ett svårare livsval nu än tidigare. Men människor har alltid sökt efter en mening med livet och hållbara svar. Genom att viga ditt liv till Gud kan han på ett särskilt sätt använda dig som ett redskap för att nå människor som söker honom.
                </AutoTranslate>
              </p>
              <p className="text-xl font-semibold text-white">
                <AutoTranslate>Kallelsen är en fantastisk gåva ifrån Gud.</AutoTranslate>
              </p>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KallelsePage;
